/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Heading,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Text,
  HStack,
  Select,
  Input,
  Button,
  Stack,
  IconButton,
  useToast,
  useColorModeValue,
  Flex,
  Spacer,
} from "@chakra-ui/react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiExternalLink,
  FiDownload,
} from "react-icons/fi";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { BASE_URL } from "../../services/api.js";
import CreateTransactionModal from "./CreateTransactionModal.jsx";
import { useUser } from "../../context/UserContext.js";

// Helper: build query string
const buildQuery = (params) =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");

// helper to normalize server response
function normalizePayload(payload) {
  if (payload.items && Array.isArray(payload.items)) {
    return { items: payload.items, total: payload.total ?? null };
  } else if (payload.success && Array.isArray(payload.data)) {
    return { items: payload.data, total: payload.total ?? payload.data.length };
  } else if (Array.isArray(payload)) {
    return { items: payload, total: payload.length };
  } else {
    const arr = payload.data || payload.items;
    if (Array.isArray(arr))
      return { items: arr, total: payload.total ?? arr.length };
  }
  throw new Error("Unexpected response format from server");
}

// client-side filter function (works with payments[] or legacy shape)
function applyClientFilters(
  items,
  { paymentType, status, startDate, endDate }
) {
  return items.filter((t) => {
    // paymentType: match if any payment line has that type OR legacy paymentType equals it
    if (paymentType) {
      const pType = (paymentType || "").toUpperCase();
      const has =
        (Array.isArray(t.payments) &&
          t.payments.some(
            (p) => (p.paymentType || "").toUpperCase() === pType
          )) ||
        (t.paymentType || "").toUpperCase() === pType;
      if (!has) return false;
    }

    // status
    if (status && (t.status || "").toUpperCase() !== status.toUpperCase())
      return false;

    // date range: createdAt is compared inclusively (YYYY-MM-DD)
    if (startDate) {
      const st = new Date(startDate + "T00:00:00");
      const created = t.createdAt ? new Date(t.createdAt) : null;
      if (!created || created < st) return false;
    }
    if (endDate) {
      // include the full end day
      const ed = new Date(endDate + "T23:59:59.999");
      const created = t.createdAt ? new Date(t.createdAt) : null;
      if (!created || created > ed) return false;
    }

    return true;
  });
}

export default function TransactionsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { loggedInUser } = useUser();

  const initialPosCounterId =
    searchParams.get("posCounterId") ||
    (location.state && location.state.posCounterId) ||
    "";

  const [posCounterId] = useState(initialPosCounterId);

  // ui state
  const [transactions, setTransactions] = useState([]); // holds server-returned items (either page or full result)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // pagination + filters
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(null); // server total for unfiltered mode; in filtered mode we'll set total to filteredCount

  const [paymentType, setPaymentType] = useState(""); // CASH|UPI|CARD|CREDIT
  const [status, setStatus] = useState(""); // PENDING|SUCCESS|FAILED|REFUNDED
  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
  const [endDate, setEndDate] = useState("");
  const [sort, setSort] = useState("createdAt:desc");
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const bg = useColorModeValue("white", "gray.800");

  // detect filtering mode: if any filter is active -> client fetch-all + client-side filter
  const isFiltering = Boolean(paymentType || status || startDate || endDate);

  // pageCount is computed from the "current total" (filtered or server total)
  const pageCount = useMemo(() => {
    if (total === null) return null;
    return Math.ceil(total / limit);
  }, [total, limit]);

  useEffect(() => {
    // when filters change, reset to page 1
    setPage(1);
  }, [paymentType, status, startDate, endDate, limit]);

  // fetchTransactions - will fetch either a page (normal) or a large set (when filtering)
  async function fetchTransactions(signal, opts = {}) {
    setLoading(true);
    setError(null);

    try {
      const usePage = opts.pageOverride ?? page;
      // if filtering, fetch a large limit (one-shot); else use current limit
      const useLimit = opts.limitOverride ?? (isFiltering ? 100000 : limit);

      // build params WITHOUT paymentType in query (we may put paymentType in path)
      const params = {
        page: usePage,
        limit: useLimit,
        sort,
        status: isFiltering ? undefined : status || undefined, // if filtering client-side, don't send status to server (we'll handle)
        startDate: isFiltering ? undefined : startDate || undefined,
        endDate: isFiltering ? undefined : endDate || undefined,
      };

      let url;
      const qs = buildQuery(params);

      // Choose endpoint based on whether paymentType & posCounterId are present
      // If filtering client-side we still prefer generic endpoints (avoid server-side partial filter mismatch)
      if (!isFiltering && posCounterId && paymentType) {
        // backend: GET /transactions/pos/:posCounterId/paymentType/:paymentType
        url = `${BASE_URL}/api/transactions/pos/${encodeURIComponent(
          posCounterId
        )}/paymentType/${encodeURIComponent(paymentType)}${qs ? `?${qs}` : ""}`;
      } else if (posCounterId) {
        // backend: GET /transactions/pos/:posCounterId
        url = `${BASE_URL}/api/transactions/pos/${encodeURIComponent(
          posCounterId
        )}${qs ? `?${qs}` : ""}`;
      } else if (!isFiltering && paymentType) {
        // backend: GET /transactions/paymentType/:paymentType
        url = `${BASE_URL}/api/transactions/paymentType/${encodeURIComponent(
          paymentType
        )}${qs ? `?${qs}` : ""}`;
      } else {
        // generic list endpoint
        url = `${BASE_URL}/api/transactions${qs ? `?${qs}` : ""}`;
      }

      console.log("fetchTransactions url:", url, "isFiltering:", isFiltering);

      const res = await fetch(url, { signal });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Server responded ${res.status}: ${text}`);
      }
      const payload = await res.json();
      const { items, total: tot } = normalizePayload(payload);

      // If we're in filtering mode: apply client-side filters across the fetched items (we fetched a big set)
      if (isFiltering) {
        // we may have fetched the whole collection (useLimit large), so apply client filters
        const filtered = applyClientFilters(items, {
          paymentType,
          status,
          startDate,
          endDate,
        });
        setTransactions(filtered);
        setTotal(filtered.length);
      } else {
        // not filtering: server handles paging/filters
        setTransactions(items);
        setTotal(tot);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("fetchTransactions:", err);
        setError(err.message || "Failed to load transactions");
        toast({
          title: "Error",
          description: err.message || "Failed to load transactions",
          status: "error",
          duration: 4000,
        });
      }
    } finally {
      setLoading(false);
    }
  }

  // Initial + reactive fetch
  useEffect(() => {
    const controller = new AbortController();
    // if filtering: request a large limit so we can filter client-side across full set
    const opts = isFiltering ? { pageOverride: 1, limitOverride: 100000 } : {};
    fetchTransactions(controller.signal, opts);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    limit,
    // intentionally include filters so effect runs when they change
    paymentType,
    status,
    startDate,
    endDate,
    posCounterId,
    sort,
    isFiltering,
  ]);

  // compute displayed rows: if filtering mode, we do client-side pagination from transactions[].
  // if not filtering, transactions[] already represents the server page.
  const displayedTransactions = useMemo(() => {
    if (isFiltering) {
      const start = (page - 1) * limit;
      return transactions.slice(start, start + limit);
    }
    return transactions;
  }, [transactions, isFiltering, page, limit]);

  const onRowOpen = (tx) => {
    navigate(`/transactions/${tx._id}`, { state: { transaction: tx } });
  };

  // CSV download: always request a large set from server using current query semantics, then apply client filters (if any)
  const handleDownloadCSV = async () => {
    try {
      toast({ title: "Preparing export...", status: "info", duration: 2000 });

      const bigLimit = 100000;
      // Build server params: when not filtering we prefer server-side filters for efficiency;
      // when filtering we fetch a large set then apply client filters again to be safe.
      const params = {
        page: 1,
        limit: bigLimit,
        sort,
        // do NOT send paymentType in query string; we use path or client filtering below
        status: isFiltering ? undefined : status || undefined,
        startDate: isFiltering ? undefined : startDate || undefined,
        endDate: isFiltering ? undefined : endDate || undefined,
      };
      const qs = buildQuery(params);

      let url;
      if (!isFiltering && posCounterId && paymentType) {
        url = `${BASE_URL}/transactions/pos/${encodeURIComponent(
          posCounterId
        )}/paymentType/${encodeURIComponent(paymentType)}${qs ? `?${qs}` : ""}`;
      } else if (posCounterId) {
        url = `${BASE_URL}/transactions/pos/${encodeURIComponent(
          posCounterId
        )}${qs ? `?${qs}` : ""}`;
      } else if (!isFiltering && paymentType) {
        url = `${BASE_URL}/transactions/paymentType/${encodeURIComponent(
          paymentType
        )}${qs ? `?${qs}` : ""}`;
      } else {
        url = `${BASE_URL}/transactions${qs ? `?${qs}` : ""}`;
      }

      const res = await fetch(url);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Export server responded ${res.status}: ${text}`);
      }
      const payload = await res.json();
      const { items: exportItems } = normalizePayload(payload);

      // apply client filters if filtering mode (or if server didn't filter paymentType)
      const finalItems = isFiltering
        ? applyClientFilters(exportItems, {
            paymentType,
            status,
            startDate,
            endDate,
          })
        : exportItems;

      if (!finalItems || finalItems.length === 0) {
        return toast({ title: "No rows to export", status: "warning" });
      }

      // Build CSV
      const header = [
        "createdAt",
        "orderId",
        "totalAmount",
        "paymentTypes",
        "paymentAmounts",
        "instruments",
        "transactionRefs",
        "posCounterId",
        "handledBy",
        "status",
        "notes",
        "_id",
      ];

      const escape = (v) => {
        if (v === null || v === undefined) return "";
        const s = String(v);
        if (s.includes('"') || s.includes(",") || s.includes("\n")) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      };

      const lines = [header.join(",")];

      for (const t of finalItems) {
        const payments =
          Array.isArray(t.payments) && t.payments.length > 0
            ? t.payments
            : t.paymentType
            ? [
                {
                  paymentType: t.paymentType,
                  amount: t.amount,
                  instrument: t.instrument,
                  transactionRef: t.transactionRef,
                },
              ]
            : [];

        const paymentTypesStr = payments
          .map((p) => p.paymentType || "")
          .join(" | ");
        const paymentAmountsStr = payments
          .map((p) => p.amount ?? "")
          .join(" | ");
        const instrumentsStr = payments
          .map((p) => {
            const inst = p.instrument || {};
            if (inst.cardLast4) return `Card •••• ${inst.cardLast4}`;
            if (inst.upiVpa) return `UPI ${inst.upiVpa}`;
            return "-";
          })
          .join(" | ");
        const refsStr = payments.map((p) => p.transactionRef || "").join(" | ");

        const row = [
          escape(t.createdAt ? new Date(t.createdAt).toISOString() : ""),
          escape(t.orderId ?? ""),
          escape(t.totalAmount ?? t.amount ?? ""),
          escape(paymentTypesStr),
          escape(paymentAmountsStr),
          escape(instrumentsStr),
          escape(refsStr),
          escape(t.posCounterId ?? ""),
          escape(
            typeof t.handledBy === "object" && t.handledBy?.name
              ? t.handledBy.name
              : t.handledBy ?? ""
          ),
          escape(t.status ?? ""),
          escape(t.notes ?? ""),
          escape(t._id ?? ""),
        ];
        lines.push(row.join(","));
      }

      const csvContent = lines.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const urlBlob = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = urlBlob;
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      a.download = `transactions_export_${timestamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(urlBlob);

      toast({ title: "Export ready", status: "success", duration: 3000 });
    } catch (err) {
      console.error("export error:", err);
      toast({
        title: "Export failed",
        description: err.message || String(err),
        status: "error",
        duration: 4000,
      });
    }
  };

  return (
    <Box p={6} minH="100vh">
      <Stack direction="row" justify="space-between" align="center" mb={6}>
        <Heading size="md">
          Transactions {posCounterId ? ` — Counter ${posCounterId}` : ""}
        </Heading>
        <HStack spacing={2}>
          <Button size="sm" onClick={() => navigate(-1)}>
            Back
          </Button>

          <Button
            colorScheme="teal"
            size="sm"
            onClick={() => setIsCreateOpen(true)}
          >
            + New Transaction
          </Button>

          <Button
            size="sm"
            leftIcon={<FiDownload />}
            onClick={handleDownloadCSV}
          >
            Export CSV
          </Button>
        </HStack>
      </Stack>

      {/* Filters */}
      <Stack
        direction={{ base: "column", md: "row" }}
        spacing={3}
        mb={4}
        align="center"
      >
        <Select
          placeholder="Payment type"
          value={paymentType}
          onChange={(e) => setPaymentType(e.target.value)}
          maxW="200px"
        >
          <option value="CASH">CASH</option>
          <option value="UPI">UPI</option>
          <option value="CARD">CARD</option>
          <option value="CREDIT">CREDIT</option>
        </Select>

        <Select
          placeholder="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          maxW="200px"
        >
          <option value="PENDING">PENDING</option>
          <option value="SUCCESS">SUCCESS</option>
          <option value="FAILED">FAILED</option>
          <option value="REFUNDED">REFUNDED</option>
        </Select>

        <Input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          maxW="200px"
        />
        <Input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          maxW="200px"
        />

        <Select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          maxW="120px"
        >
          <option value={10}>10 / page</option>
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
        </Select>

        <Spacer />

        <Select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          maxW="220px"
        >
          <option value="createdAt:desc">Newest first</option>
          <option value="createdAt:asc">Oldest first</option>
          <option value="amount:desc">Amount: high → low</option>
          <option value="amount:asc">Amount: low → high</option>
        </Select>
      </Stack>

      {/* Content */}
      {loading ? (
        <Center py={20}>
          <Spinner size="lg" />
        </Center>
      ) : error ? (
        <Center py={20}>
          <Text color="red.500">Error: {error}</Text>
        </Center>
      ) : (
        <>
          <Box borderWidth="1px" borderRadius="md" overflowX="auto" bg={bg}>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Order</Th>
                  <Th>Amount</Th>
                  <Th>Type(s)</Th>
                  <Th>Instrument(s)</Th>
                  <Th>Handler</Th>
                  <Th>Status</Th>
                  <Th>Ref(s)</Th>
                  <Th>Notes</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {displayedTransactions.map((t) => {
                  // support both old shape and new shape
                  const payments =
                    Array.isArray(t.payments) && t.payments.length > 0
                      ? t.payments
                      : t.paymentType
                      ? [
                          {
                            paymentType: t.paymentType,
                            amount: t.amount,
                            instrument: t.instrument,
                            transactionRef: t.transactionRef,
                          },
                        ]
                      : [];

                  const types = payments
                    .map((p) => p.paymentType)
                    .filter(Boolean);
                  const typeTags = [...new Set(types)]; // unique

                  const firstInstrument = payments.find(
                    (p) =>
                      p.instrument &&
                      (p.instrument.cardLast4 || p.instrument.upiVpa)
                  );
                  const instrumentDisplay = firstInstrument
                    ? firstInstrument.instrument?.cardLast4
                      ? `Card •••• ${firstInstrument.instrument.cardLast4}`
                      : firstInstrument.instrument?.upiVpa ?? "-"
                    : "-";

                  const refs = payments
                    .map((p) => p.transactionRef)
                    .filter(Boolean)
                    .join(" | ");

                  // amount display prefer totalAmount
                  const amountDisplay =
                    t.totalAmount ??
                    t.amount ??
                    payments.reduce((s, p) => s + Number(p.amount || 0), 0);

                  return (
                    <Tr
                      key={t._id}
                      _hover={{
                        bg: useColorModeValue("gray.50", "gray.700"),
                        cursor: "pointer",
                      }}
                      onClick={() => onRowOpen(t)}
                    >
                      <Td>
                        {t.createdAt
                          ? new Date(t.createdAt).toLocaleString()
                          : "-"}
                      </Td>
                      <Td>
                        <Text fontSize="sm">{t.orderId}</Text>
                      </Td>
                      <Td>₹{Number(amountDisplay || 0).toLocaleString()}</Td>
                      <Td>
                        <HStack spacing={2}>
                          {typeTags.length > 0 ? (
                            typeTags.map((tt) => (
                              <Tag key={tt} size="sm">
                                {tt}
                              </Tag>
                            ))
                          ) : (
                            <Text>-</Text>
                          )}
                        </HStack>
                      </Td>
                      <Td>{instrumentDisplay}</Td>
                      <Td fontSize="sm">
                        {typeof t.handledBy === "object" && t.handledBy?.name
                          ? t.handledBy.name
                          : t.handledBy ?? "-"}
                      </Td>
                      <Td>
                        <Tag
                          size="sm"
                          colorScheme={
                            t.status === "SUCCESS"
                              ? "green"
                              : t.status === "PENDING"
                              ? "yellow"
                              : "red"
                          }
                        >
                          {t.status}
                        </Tag>
                      </Td>
                      <Td>{refs || "-"}</Td>
                      <Td maxW="220px" isTruncated>
                        {t.notes ?? "-"}
                      </Td>
                      <Td>
                        <IconButton
                          size="sm"
                          aria-label="open"
                          icon={<FiExternalLink />}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/transactions/${t._id}`, {
                              state: { transaction: t },
                            });
                          }}
                        />
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          {/* Pagination */}
          <Flex align="center" justify="space-between" mt={4}>
            <Text fontSize="sm">
              Showing {displayedTransactions.length}
              {total ? ` of ${total}` : ""} results
            </Text>

            <HStack>
              <IconButton
                aria-label="prev"
                icon={<FiChevronLeft />}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                isDisabled={page <= 1}
                size="sm"
              />
              <Text fontSize="sm">
                Page {page}
                {pageCount ? ` / ${pageCount}` : ""}
              </Text>
              <IconButton
                aria-label="next"
                icon={<FiChevronRight />}
                onClick={() =>
                  setPage((p) =>
                    pageCount ? Math.min(pageCount, p + 1) : p + 1
                  )
                }
                isDisabled={
                  pageCount
                    ? page >= pageCount
                    : displayedTransactions.length < limit
                }
                size="sm"
              />
            </HStack>
          </Flex>

          <CreateTransactionModal
            isOpen={isCreateOpen}
            onClose={() => setIsCreateOpen(false)}
            posCounterId={posCounterId}
            tenantId={"68c68cc1690c76521da83c75"} // pass the tenant
            onCreated={() => {
              // refresh list (re-fetch with current filters)
              const controller = new AbortController();
              const opts = isFiltering
                ? { pageOverride: 1, limitOverride: 100000 }
                : {};
              fetchTransactions(controller.signal, opts);
              setIsCreateOpen(false);
            }}
          />
        </>
      )}
    </Box>
  );
}
